import { useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'

const REPORT_URL = `${window.location.origin}/report`

/** Generates a QR code for the public, no-login "report an issue" page — for printing and
 * posting around the estate. Rendered entirely client-side (no backend involvement); the
 * canvas doubles as the download source for the PNG. */
export function ReportQrModal({ onClose }: { onClose: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!canvasRef.current) return
    QRCode.toCanvas(canvasRef.current, REPORT_URL, { width: 320, margin: 2 }).catch(() =>
      setError('Failed to generate QR code'),
    )
  }, [])

  function handleDownload() {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.toBlob((blob) => {
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'riverpark-report-an-issue-qr.png'
      a.click()
      URL.revokeObjectURL(url)
    }, 'image/png')
  }

  return (
    <Modal open onClose={onClose} labelledBy="report-qr-title" panelClassName="max-w-sm">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <h2 id="report-qr-title" className="font-serif text-xl text-ink">
            QR code for reporting issues
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </ModalHeader>

      <ModalBody className="flex flex-col items-center gap-4 text-center">
        <p className="font-sans text-sm text-muted-foreground">
          Print and post this around the estate. Scanning it opens a form anyone can use to report
          an issue — no login required.
        </p>
        {error ? (
          <p className="font-sans text-sm text-destructive">{error}</p>
        ) : (
          <canvas ref={canvasRef} className="rounded border border-border-2" />
        )}
        <p className="break-all font-sans text-xs text-muted-foreground">{REPORT_URL}</p>
      </ModalBody>

      <ModalFooter>
        <Button type="button" variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button type="button" onClick={handleDownload} disabled={!!error}>
          Download PNG
        </Button>
      </ModalFooter>
    </Modal>
  )
}
